/**
 * Microtask-window batch loader: per-unit callers get individual promises,
 * the backend gets one batched request per tick. The per-unit shape keeps
 * React Query caches unit-keyed; the batching keeps the network sane.
 *
 * Retry lives HERE, at the batch (transport) level — units must never retry
 * individually: unit-level retry timers fire in separate macrotasks, which
 * defeats the batching and turns one failed batch into N singleton requests.
 */
export function createBatchLoader<K, V>(
  batchFn: (keys: K[]) => Promise<Map<K, V>>,
  opts: { retries?: number; retryDelayMs?: number } = {},
) {
  const { retries = 0, retryDelayMs = 1500 } = opts;
  let pending: Array<{ key: K; resolve: (v: V) => void; reject: (e: unknown) => void }> = [];
  let scheduled = false;

  const flush = async () => {
    const batch = pending;
    pending = [];
    scheduled = false;
    let lastError: unknown;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
      if (attempt > 0) {
        await new Promise(r => setTimeout(r, retryDelayMs));
      }
      try {
        const results = await batchFn(batch.map(item => item.key));
        for (const item of batch) {
          item.resolve(results.get(item.key) as V);
        }
        return;
      } catch (error) {
        lastError = error;
      }
    }
    console.error(
      `[batchLoader] batch of ${batch.length} failed after ${retries + 1} attempt(s):`,
      lastError,
    );
    for (const item of batch) {
      item.reject(lastError);
    }
  };

  return {
    load(key: K): Promise<V> {
      return new Promise<V>((resolve, reject) => {
        pending.push({ key, resolve, reject });
        if (!scheduled) {
          scheduled = true;
          queueMicrotask(flush);
        }
      });
    },
  };
}
